import {
  getToken,
  onMessage,
  type MessagePayload,
  type Unsubscribe,
} from "firebase/messaging";
import { getMessagingLazy } from "./firebase";
import { registerPushDevice } from "../api/deviceToken";
import { config } from "../config";
import logger from "./logger";
import { useNotificationStore } from "../store/notificationStore";

const VAPID_KEY = config.firebaseVapidKey;

/** The SW can't read localStorage; persist the current user id where it can look (it drops my own chat pushes). */
function storeSwUserId(userId: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      const open = indexedDB.open("whoisit-sw", 1);
      open.onupgradeneeded = () => open.result.createObjectStore("kv");
      open.onsuccess = () => {
        const tx = open.result.transaction("kv", "readwrite");
        tx.objectStore("kv").put(userId, "userId");
        tx.oncomplete = () => {
          open.result.close();
          resolve();
        };
        tx.onabort = () => {
          open.result.close();
          resolve();
        };
      };
      open.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

/**
 * Push is on when the browser's notification permission is granted: the browser's own answer is the consent
 * (controller ruling, privacy consent fix round 1). Such browsers register on load exactly as before, without a
 * prompt; browsers still at "default" are asked only from a tap (NotificationPrePrompt).
 */
export function isPushEnabled(): boolean {
  return typeof Notification !== "undefined" && Notification.permission === "granted";
}

/**
 * Asks the browser for notification permission and registers this browser for push. Call ONLY from a user tap
 * (NotificationPrePrompt "Turn on"); never on page load.
 */
export async function requestNotificationPermission(
  userId: string,
): Promise<string | null> {
  logger.info("[FCM] requestNotificationPermission called", {
    userId,
    hasServiceWorker: !!navigator.serviceWorker,
  });
  if (!navigator.serviceWorker) {
    logger.warn(
      "[FCM] Aborted: serviceWorker unavailable — app must be served over HTTPS or localhost",
    );
    return null;
  }
  // Probe messaging first: an unsupported browser is never asked for a permission it could not use.
  if (!(await getMessagingLazy())) {
    logger.warn(
      "[FCM] Aborted: Firebase messaging not initialized — check Firebase config and secure context",
    );
    return null;
  }
  try {
    const permission = await Notification.requestPermission();
    logger.debug("[FCM] Notification permission:", permission);
    if (permission !== "granted") return null;
    return await registerPushToken(userId);
  } catch (e) {
    logger.warn("[FCM] Registration failed", e);
    return null;
  }
}

/** On load: registers (or refreshes the token) only when the permission is already granted; never prompts. */
export async function registerPushIfEnabled(
  userId: string,
): Promise<string | null> {
  if (!isPushEnabled() || !navigator.serviceWorker) return null;
  try {
    return await registerPushToken(userId);
  } catch (e) {
    logger.warn("[FCM] Registration failed", e);
    return null;
  }
}

/** Needs the permission already granted; never prompts. */
async function registerPushToken(userId: string): Promise<string | null> {
  const messaging = await getMessagingLazy();
  if (!messaging) {
    logger.warn(
      "[FCM] Aborted: Firebase messaging not initialized — check Firebase config and secure context",
    );
    return null;
  }

  await storeSwUserId(userId);

  // updateViaCache:"none" stops the browser pinning importScripts() results
  // at install time, which would otherwise serve stale runtime-env.js forever.
  await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
    updateViaCache: "none",
  });
  const swRegistration = await navigator.serviceWorker.ready;
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: swRegistration,
  });
  logger.debug("[FCM] Got token:", token?.slice(0, 20) + "...");

  const tokenKey = `fcm_token_${userId}`;
  const stored = localStorage.getItem(tokenKey);
  if (token !== stored) {
    logger.debug("[FCM] Token is new — registering with backend");
    await registerPushDevice(token);
    localStorage.setItem(tokenKey, token);
    logger.debug("[FCM] Registered with pusher-stc");
  } else {
    logger.debug("[FCM] Token unchanged — skipping registration");
  }

  return token;
}

export function onForegroundMessage(
  callback: (payload: MessagePayload) => void,
): Unsubscribe {
  // Only when push is on (messaging is never created before the user's tap). Messaging resolves after the support
  // probe; subscribe then. Unsubscribing earlier cancels it.
  let cancelled = false;
  let unsubscribe: Unsubscribe | null = null;
  if (!isPushEnabled()) return () => {};
  void getMessagingLazy().then((messaging) => {
    if (cancelled || !messaging) return;
    unsubscribe = onMessage(messaging, (payload) => {
      logger.info("[FCM] Foreground message received", payload);
      callback(payload);
    });
  });
  return () => {
    cancelled = true;
    unsubscribe?.();
    unsubscribe = null;
  };
}

/** Tag shared with public/firebase-messaging-sw.js — keep both in sync. */
export function convTag(convId: string): string {
  return `conv-${convId}`;
}

/**
 * Clears everything the user could still see for one conversation:
 * the in-app toast and any OS notification the service worker posted.
 * Never rejects — a missing/blocked service worker just means nothing to close.
 */
export async function clearConversationNotifications(
  convId: string,
): Promise<void> {
  if (!convId) return;
  try {
    useNotificationStore.getState().dismissConversation(convId);
    if (!navigator.serviceWorker) return;
    const registration = await navigator.serviceWorker.ready;
    const list = await registration.getNotifications({ tag: convTag(convId) });
    list.forEach((n) => n.close());
  } catch (e) {
    logger.debug("[FCM] clearConversationNotifications failed", e);
  }
}
